import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { graphql } from '@/gql';
import { Button } from '@/components/ui/Button';
import { FormField, FormLayout, FormSection } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { useSession } from '@/features/session/useSession';

const UpdateScopeBeaconDoc = graphql(/* GraphQL */ `
  mutation UpdateScopeBeaconMajor($id: ID!, $beaconMajor: Int) {
    updateScope(id: $id, input: { beaconMajor: $beaconMajor }) {
      id
      name
      beaconMajor
    }
  }
`);

/**
 * Current-scope iBeacon Major (loyalty.md 3.3). ExpoApp ranges UUID-only and
 * matches this on-device to pin that venue's vouchers. Presence never leaves
 * the phone.
 */
export function VenueTab() {
  const { me, refresh } = useSession();
  const scope = me?.currentScope;
  const [major, setMajor] = useState(
    scope?.beaconMajor != null ? String(scope.beaconMajor) : '',
  );
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (scope && seededFor !== scope.id) {
    setSeededFor(scope.id);
    setMajor(scope.beaconMajor != null ? String(scope.beaconMajor) : '');
  }
  const [save, { loading: saving }] = useMutation(UpdateScopeBeaconDoc);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!scope) {
    return <p className="text-sm text-gray-500">No active venue in this session.</p>;
  }

  return (
    <FormLayout
      title="Venue beacon"
      description={`${scope.name}: iBeacon Major for this venue (0–65535). Same UUID on every Ferry beacon; Major distinguishes the club. Leave empty if this venue has no beacon. Changing this does not log guest presence.`}
      onSubmit={async (event) => {
        event.preventDefault();
        setSaveError(null);
        const trimmed = major.trim();
        let beaconMajor: number | null = null;
        if (trimmed !== '') {
          const parsed = Number.parseInt(trimmed, 10);
          if (!Number.isInteger(parsed) || parsed < 0 || parsed > 65535) {
            setSaveError('Major must be a whole number from 0 to 65535, or empty.');
            return;
          }
          beaconMajor = parsed;
        }
        try {
          await save({
            variables: { id: scope.id, beaconMajor },
          });
          await refresh();
        } catch (err) {
          setSaveError(graphqlErrorMessage(err));
        }
      }}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" id="admin.config.venueBeaconSave" loading={saving}>
            Save beacon
          </Button>
          {saveError ? <span className="text-sm text-red-600">{saveError}</span> : null}
        </div>
      }
    >
      <FormSection title="iBeacon">
        <FormField
          label="Major"
          description="Ferry = 1, Elio = 2. Unique across venues. Clear the field to unassign."
        >
          <Input
            id="admin.config.venueBeaconMajor"
            type="number"
            min={0}
            max={65535}
            step={1}
            value={major}
            onChange={(e) => setMajor(e.target.value)}
            placeholder="e.g. 1"
          />
        </FormField>
      </FormSection>
    </FormLayout>
  );
}
